/* R796 Battlegrounds page: the one API client (contract §7.3).
   Every server call of the game page goes through here: the world doors
   (hoh_bgw_*), the Endless run doors a level run reuses (state, continue, End
   run) and the Worker (/contract-settle). No supabase-js: plain fetch with the
   same publishable key the Hall uses, so there is one value to rotate.

   Session  the Hall's own token, localStorage hoh-your-hall-token {email, pin},
            read only and never written. No token or no PIN means signed out.
   Writes   every write door takes p_request_id = 'bgw:<door>:<uuid>'. The id is
            kept in a sessionStorage journal (bgw-journal-v1) from the tap until
            a definite reply, so a retry of the same intent sends the same id and
            the server answers it from its saved receipt instead of doing it
            twice. A success or a refusal clears it; weather keeps it.
   Errors   errorKind(err) is runErrorKind of hoh-contract-data.js (R791), copied
            as pure functions: account, reload, engine, server, transient. A door
            that answers ok:false becomes an error whose settleCode is its reason,
            classified server, except bad_account (account) and busy (a lock
            timeout, transient).
   Replies  resolve only with ok:true. Nothing here computes gold, stars, odds or
            rolls: the page shows what the reply said. */

const TOKEN_KEY = 'hoh-your-hall-token';
const JOURNAL_KEY = 'bgw-journal-v1';
const CATALOG_PREFIX = 'bgw-catalog-';
const CATALOG_LAST = 'bgw-catalog-last';
const SESSION_MS = 365 * 24 * 60 * 60 * 1000;       /* the Hall's sliding session (hoh-your-hall.js) */
const JOURNAL_TTL_MS = 6 * 60 * 60 * 1000;
const ENDLESS_MIN_BUILD = 141;                        /* hoh-contract-data.js MIN_WORKER_BUILD */
const LEVEL_MIN_BUILD = 143;
const DEFAULT_TIMEOUTS = Object.freeze({ rpc: 8000, long: 12000, worker: 20000, gate: 6000 });
const UPGRADE_ACTIONS = Object.freeze(['reconcile', 'offer', 'activate', 'reserve', 'choose']);
/* the Hall grid's public read, cut to what a face needs: never an email, owner or story column */
const HALL_COLS = 'id,name,image_url,image_thumb_url,is_ff_portrait,image_offset_x,image_offset_y,image_zoom';
/* the hosted battle's Hall pool (window.hohGetAllCharacters): the public columns its faces read, never an email, owner or PIN column */
const ROSTER_COLS = 'id,name,race,character_class,hearts,image_url,image_thumb_url,is_ff_portrait,image_offset_x,image_offset_y,image_zoom';

/* ============ R791 · ONE READING OF A FAILURE (copied, pure) ==============
   The rules below are hoh-contract-data.js runErrorKind and its helpers,
   byte for byte in behaviour: account, reload, engine, server, transient. */
const RUN_ENGINE_CODE_RE = /^(?:upgrade\d+:[a-z0-9_]+|invalid_[a-z0-9_]+|special_reward_[a-z0-9_]+|combat_termination_[a-z0-9_]+|encounter_(?:queue|phase)_limit|engine_not_released|unresolved_rule[a-z0-9_]*|shell_reward_[a-z0-9_]+|vanguard_reward_[a-z0-9_]+|unknown_(?:unit_archetype|card_op|ability_op|unlimited_buff_fx)|unsupported_(?:battleground_[a-z_]+|mode_for_engine)|unlimited_segment_crosses_stop)/i;
function settleTransportFailure(err) {
  if (!err) return false;
  const msg = String(err.message || '');
  if (err.name === 'AbortError' || err.__hohTimeout || /signal is aborted/i.test(msg)) return true;
  if (err.name === 'TypeError' && /fetch|network|load failed/i.test(msg)) return true;
  if (/^bad settle body/.test(msg)) return true;
  const s = err.httpStatus | 0;
  return (s === 408 || s === 425 || s === 429 || s >= 500) && !err.settleDetail && !err.__hocDeterministic;
}
const RUN_RECOVERABLE_RE = /^upgrade_(?:committed_plays_changed|choice_required)$/i;
function runServerDeterministic(e) {
  if (!e) return false;
  if (RUN_RECOVERABLE_RE.test(String(e.settleCode || '')) || RUN_RECOVERABLE_RE.test(String(e.settleDetail || '')) ||
      RUN_RECOVERABLE_RE.test(String(e.__hocRefused || ''))) return false;
  if (e.__hocDeterministic === true || e.__hohServerDeterministic === true) return true;
  if (e.settleCode === 'engine_refused' || e.__hocRefused === 'engine_refused') return true;
  const d = e.__hocDetailRemembered ? '' : String(e.settleDetail || ''), c = String(e.settleCode || '');
  if (d && (RUN_ENGINE_CODE_RE.test(d) || /^upgrade_[a-z0-9_]+/i.test(d))) return true;
  if (c === 'upgrade_checkpoint_temporarily_unavailable') return !!d;
  return /^upgrade_[a-z0-9_]+$/i.test(c);
}
function trimRunCode(value) {
  /* BG-SYNC (2026-10-10): a team the engine refused keeps the body it named, so the code says which hero to look at
     ('invalid_world_body:recruits:0'); every other code trims exactly as before */
  const m = /^([a-z][a-z0-9_]*)(?::([a-z][a-z0-9]*_[a-z0-9_]+))?(?::(roster|recruits|campfire):([0-9]{1,3})(?![0-9]))?/i.exec(String(value || '').trim());
  return m ? (m[1] + (m[2] ? ':' + m[2] : '') + (m[3] ? ':' + m[3] + ':' + m[4] : '')).slice(0, 64) : '';
}
function codeLikeRunText(msg) {
  const s = String(msg || '').trim();
  return s.length <= 160 && s.indexOf('_') >= 0 && /^[a-z][a-z0-9_]*(?::[a-z0-9_:.-]*)?$/i.test(s);
}
function runErrorCode(err) {
  const e = (err && typeof err === 'object') ? err : {};
  const msg = String(e.message || (typeof err === 'string' ? err : '') || '').trim();
  if (e.__hohFaultCode) return trimRunCode(e.__hohFaultCode);
  if ((e.__hohEngine || RUN_ENGINE_CODE_RE.test(msg)) && codeLikeRunText(msg.split(' ')[0])) return trimRunCode(msg);
  if (e.__hohEngine) return e.name === 'TypeError' ? 'engine_type_error' : 'engine_error';
  if (e.settleDetail && !e.__hocDetailRemembered && codeLikeRunText(e.settleDetail)) return trimRunCode(e.settleDetail);
  const named = String(e.settleCode || e.__hocRefused || '');
  if (named && named !== 'refused' && codeLikeRunText(named)) return trimRunCode(named);
  if (e.__hohRpcName && e.__hohRpcCode) return 'db' + String(e.__hohRpcCode).replace(/[^a-z0-9]/gi, '').slice(0, 12);
  if (e.name === 'AbortError' || e.__hohTimeout || /signal is aborted/i.test(msg)) return 'timeout';
  if (e.name === 'TypeError' && /fetch|network|load failed/i.test(msg)) return 'offline';
  if (e.httpStatus) return 'http' + (e.httpStatus | 0);
  return codeLikeRunText(msg) ? trimRunCode(msg) : '';
}
function runErrorKind(err) {
  const e = (err && typeof err === 'object') ? err : {};
  const msg = String(e.message || (typeof err === 'string' ? err : '') || '');
  const code = runErrorCode(err);
  let kind = 'transient';
  if (e.__hohAccountChanged || e.httpStatus === 401 || e.httpStatus === 403 || /^sign in first$/i.test(msg)) kind = 'account';
  else if (e.__hohReload || /^unsupported_engine_version:/.test(msg)) kind = 'reload';
  else if (e.__hohEngine || RUN_ENGINE_CODE_RE.test(msg)) kind = 'engine';
  else if (runServerDeterministic(e)) kind = 'server';
  return { kind: kind, code: code, deterministic: kind === 'engine' || kind === 'server', transport: settleTransportFailure(e) };
}
function runSupportCode(err, runId, prefix) {
  let code = runErrorCode(err);
  if (!code) return '';
  const parts = code.split(':');
  if (parts.length > 1 && /\d$/.test(parts[0])) parts.shift();
  code = parts.join('.').replace(/[^a-z0-9_.-]/gi, '').slice(0, 40);
  const id = String(runId == null ? '' : runId).replace(/[^a-z0-9_-]/gi, '').slice(0, 20);
  return (prefix || 'BG') + '-' + (id ? id + '-' : '') + code;
}
function plainRunCopy(msg, err) {
  if (!msg || msg.length > 280) return false;
  if (err && err.__hohEngine) return false;
  if (err && err.__hohRpcName && String(err.__hohRpcCode || '') !== 'P0001') return false;
  if (!/^[A-Z0-9"'“‘]/.test(msg) || msg.indexOf(' ') < 0 || !/[.!?)]$/.test(msg)) return false;
  return !/violates|constraint|relation "|column "|function |syntax error|permission denied|JWT|PGRST|duplicate key|\bnull\b|\bundefined\b|is not a function|Cannot read|Unexpected token|JSON|signal is aborted|Failed to fetch|Load failed|NetworkError|\(\d{3}\)|[a-z0-9]_[a-z0-9]|::|[{}<>]|https?:/i.test(msg);
}
export const RUN_LINES = Object.freeze({
  end: 'The run could not be ended just now. Your banked rewards are safe. Try again.',
  start: 'That run could not start. Try again.',
  check: 'Your run could not be checked. Try again.',
  prepare: 'The next round could not start. Check your connection and try again.',
  shop: 'The shop could not be reached. Try again.',
  upgrade: 'The upgrade could not be saved. Your run is still saved.',
  claim: 'That upgrade could not be claimed. Nothing was spent.',
  quest: 'The quest could not be saved. Try again.',
  save: 'That could not be saved. Try again.',
  generic: 'Something went wrong. Try again.'
});
const WORLD_LINES = Object.freeze({ home: 'The Battlegrounds could not load. Try again.' });

/* {kind, code, deterministic, transport} by the R791 rules, plus the door's
   own refusal reason (or '') so a screen can pick its words */
export function errorKind(err) {
  const k = runErrorKind(err);
  k.reason = (err && typeof err === 'object' && err.bgReason) ? String(err.bgReason) : '';
  return k;
}

/* The line a player reads. Plain copy stays; anything machine-shaped becomes
   the context's line, plus BG-<run>-<code> when it is not just weather. */
export function playerLine(err, ctx) {
  const c = typeof ctx === 'string' ? { context: ctx } : (ctx || {});
  const context = String(c.context || '');
  const base = c.line || RUN_LINES[context] || WORLD_LINES[context] || RUN_LINES.generic;
  const msg = String((err && err.message) || (typeof err === 'string' ? err : '') || '').trim();
  const info = runErrorKind(err);
  if (plainRunCopy(msg, err)) return msg;
  if (info.kind === 'account') return 'Sign in again to continue.';
  if (info.transport) {
    return (context === 'end' || context === 'flee')
      ? 'The game server did not answer. Your banked rewards are safe. Try again.' : base;
  }
  const prefix = c.prefix || ((context === 'claim' || context === 'quest') ? 'HALL' : 'BG');
  const code = runSupportCode(err, c.runId, prefix);
  return code ? base + ' (Code ' + code + ')' : base;
}

/* ---------- ids ---------- */
function uuid() {
  try { if (crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID(); } catch (_) {}
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
  return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
}
export function newRequestId(door) {
  const d = String(door || 'write').toLowerCase().replace(/^hoh_bgw_/, '').replace(/[^a-z0-9_]/g, '').slice(0, 40) || 'write';
  return 'bgw:' + d + ':' + uuid();
}

/* two independent 32-bit hashes: a journal key collision would replay the
   wrong receipt, so 64 bits, never 32 */
function fnv1a(s) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0).toString(16).padStart(8, '0');
}
function djb2(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 33) ^ s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(16).padStart(8, '0');
}
function stable(v) {
  if (v === undefined) return 'null';
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return '[' + v.map(stable).join(',') + ']';
  return '{' + Object.keys(v).sort().filter(k => v[k] !== undefined).map(k => JSON.stringify(k) + ':' + stable(v[k])).join(',') + '}';
}

/* the Pacific day the daily gift belongs to (pack-odds.json daily_gift.day) */
function pacificDay() {
  try { return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()); }
  catch (_) { return new Date().toISOString().slice(0, 10); }
}
/* one Hall row as a face: the image, the thumbnail, and the Hall's own framing values
   (hohGetImageOffsetX/Y and hohGetImageZoom: 0-100 on each axis, 50 centred; zoom 100-200) */
function hallFace(row) {
  if (!row || typeof row !== 'object' || row.id == null) return null;
  const img = String(row.image_url || row.image_thumb_url || ''), thumb = String(row.image_thumb_url || row.image_url || '');
  if (!img) return null;
  const n = v => (v == null || v === '' ? NaN : Number(v));
  const x = n(row.image_offset_x), y = n(row.image_offset_y), z = n(row.image_zoom);
  /* 2026-09-30 (PERF-2026-09-30 item 3): the face's stored thumbnail, by its full image, so a small face (bg-ui portrait)
     decodes the 320 px thumbnail instead of the 768 px card image. Both are the same square crop (hoh-tools prepareImages). */
  if (thumb && thumb !== img) { try { (window.__bgThumbOf || (window.__bgThumbOf = new Map())).set(img, thumb); } catch (_) {} }
  return { character_id: String(row.id), name: String(row.name || '').trim() || 'Hero', img: img, thumb: thumb, is_ff: row.is_ff_portrait === true,
    frame: { x: x >= 0 && x <= 100 ? x : 50, y: y >= 0 && y <= 100 ? y : 50, zoom: z >= 100 && z <= 200 ? z : 100 } };
}

/* ---------- session (read only) ---------- */
function readToken() {
  try {
    const t = JSON.parse(localStorage.getItem(TOKEN_KEY) || 'null');
    if (!t || typeof t !== 'object') return null;
    if (t.savedAt && Date.now() - Number(t.savedAt) > SESSION_MS) return null;
    return t;
  } catch (_) { return null; }
}
function creds() {
  const t = readToken();
  const email = String((t && t.email) || '').trim().toLowerCase();
  const pin = t && t.pin != null ? String(t.pin) : '';
  return { email: email, pin: pin, ok: !!(email && pin), account: email ? 'a' + fnv1a(email) + djb2(email) : '' };
}

/* ---------- journal ---------- */
function journalRead() {
  try {
    const j = JSON.parse(sessionStorage.getItem(JOURNAL_KEY) || '{}');
    return j && typeof j === 'object' && !Array.isArray(j) ? j : {};
  } catch (_) { return {}; }
}
function journalWrite(j) {
  try { sessionStorage.setItem(JOURNAL_KEY, JSON.stringify(j)); } catch (_) {}
}
function journalTake(account, door, intent, scope) {
  const j = journalRead(), now = Date.now();
  for (const k of Object.keys(j)) if (!j[k] || !j[k].id || now - (j[k].at || 0) > JOURNAL_TTL_MS) delete j[k];
  const text = stable(intent);
  const key = account + '|' + door + '|' + fnv1a(text) + djb2(text) + ':' + text.length;
  /* a set door (a star, a boss's poster): a newer intent on the same thing supersedes an older
     unanswered one, so tapping back to the first value never replays that stale receipt */
  if (scope) for (const k of Object.keys(j)) if (k !== key && k.indexOf(account + '|') === 0 && j[k].door === door && j[k].scope === scope) delete j[k];
  if (j[key] && j[key].id) { journalWrite(j); return { key: key, id: j[key].id, reused: true }; }
  const id = newRequestId(door);
  j[key] = scope ? { id: id, door: door, at: now, scope: scope } : { id: id, door: door, at: now };
  journalWrite(j);
  return { key: key, id: id, reused: false };
}
function journalClear(key) {
  const j = journalRead();
  if (j[key]) { delete j[key]; journalWrite(j); }
}

/* ---------- storage helpers ---------- */
function lsGet(k) { try { return localStorage.getItem(k); } catch (_) { return null; } }
function lsSet(k, v) { try { localStorage.setItem(k, v); return true; } catch (_) { return false; } }
function lsDropPrefix(prefix, keep) {
  try {
    const drop = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.indexOf(prefix) === 0 && k !== keep && k !== CATALOG_LAST) drop.push(k);
    }
    drop.forEach(k => localStorage.removeItem(k));
  } catch (_) {}
}

/* ---------- errors made here ---------- */
function signInFirst() {
  const e = new Error('sign in first');
  e.__bgDefinite = true;
  return e;
}
function accountChanged() {
  const e = new Error('The signed-in account changed. Sign in again to continue.');
  e.__hohAccountChanged = true;
  e.__bgDefinite = true;
  return e;
}
function refusal(door, reply, httpStatus) {
  const reason = String((reply && (reply.reason || reply.error)) || 'refused');
  const e = new Error('refused ' + reason);
  e.settleCode = reason;
  e.__hocRefused = reason;
  e.bgReason = reason;
  e.bgDoor = door;
  e.reply = reply || null;
  e.__bgDefinite = true;
  if (httpStatus) e.httpStatus = httpStatus;
  if (reason === 'bad_account') e.__hohAccountChanged = true;
  e.__hocDeterministic = reason !== 'busy';
  if (e.__hocDeterministic) e.deterministic = true;
  return e;
}

/* The hosted battle (bg-battle) runs the Hall's own battle stack, whose data
   layer (hoh-contract-data.js) talks through window.hohSupabaseClient.rpc() and
   reads rest.url, rest.headers, rest.schemaName and supabaseKey for one beacon.
   legacyClient() is that client, built on this module's config and fetch: the
   same publishable key, no supabase-js. It resolves the supabase-js shape
   {data, error, status} and never rejects, as supabase-js does. It is not an
   API method (the contract's method table stays exact). */
export function legacyClient(config) {
  const cfg = config || {};
  const url = String(cfg.supabaseUrl || '').replace(/\/+$/, '');
  const key = String(cfg.supabaseKey || '');
  const doFetch = typeof cfg.fetch === 'function' ? cfg.fetch : (...a) => fetch(...a);
  const headers = { apikey: key, Authorization: 'Bearer ' + key };
  return Object.freeze({
    supabaseUrl: url, supabaseKey: key,
    rest: Object.freeze({ url: url + '/rest/v1', headers: headers, schemaName: 'public' }),
    rpc(fn, args) {
      return doFetch(url + '/rest/v1/rpc/' + encodeURIComponent(String(fn || '')), {
        method: 'POST', cache: 'no-store', credentials: 'omit',
        headers: Object.assign({ 'Content-Type': 'application/json', Accept: 'application/json' }, headers),
        body: JSON.stringify(args || {})
      }).then(res => res.text().then(text => {
        let json = null;
        try { json = text ? JSON.parse(text) : null; } catch (_) { json = null; }
        if (!res.ok) {
          const e = json && typeof json === 'object' && !Array.isArray(json) ? json : { message: 'rpc ' + fn, code: '' };
          return { data: null, error: e, status: res.status | 0 };
        }
        return { data: json, error: null, status: res.status | 0 };
      }), err => ({ data: null, error: { message: String((err && err.message) || 'network error'), code: '' }, status: 0 }));
    }
  });
}

export function createApi(config) {
  const cfg = Object.assign({}, config || {});
  const supabaseUrl = String(cfg.supabaseUrl || '').replace(/\/+$/, '');
  const supabaseKey = String(cfg.supabaseKey || '');
  const workerUrl = String(cfg.workerUrl || '').replace(/\/+$/, '');
  const T = Object.assign({}, DEFAULT_TIMEOUTS, cfg.timeouts || {});
  const doFetch = typeof cfg.fetch === 'function' ? cfg.fetch : (...a) => fetch(...a);
  const onPending = typeof cfg.onPending === 'function' ? cfg.onPending : null;
  const runEngines = new Map();
  const catalogMem = new Map();
  let catalogLast = null, pendingCount = 0, gateCache = null;

  function pending(delta) {
    pendingCount = Math.max(0, pendingCount + delta);
    if (onPending) { try { onPending(pendingCount); } catch (_) {} }
  }

  async function send(url, init, timeoutMs) {
    let ctl = null, timer = null, timedOut = false;
    try {
      if (typeof AbortController === 'function') {
        ctl = new AbortController();
        timer = setTimeout(() => { timedOut = true; try { ctl.abort(); } catch (_) {} }, Math.max(100, timeoutMs | 0));
      }
      const res = await doFetch(url, Object.assign({ cache: 'no-store', credentials: 'omit' }, init, ctl ? { signal: ctl.signal } : {}));
      const text = await res.text();
      let json = null;
      try { json = text ? JSON.parse(text) : null; } catch (_) { json = null; }
      return { status: res.status | 0, ok: !!res.ok, json: json };
    } catch (err) {
      if (timedOut) {
        const t = new Error('The Battlegrounds took too long to answer. Try again.');
        t.__hohTimeout = true;
        throw t;
      }
      throw err;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  /* PostgREST: errors carry the same fields as hoh-contract-data.js rpc() */
  async function rpc(name, args, timeoutMs) {
    const r = await send(supabaseUrl + '/rest/v1/rpc/' + name, {
      method: 'POST',
      headers: { apikey: supabaseKey, Authorization: 'Bearer ' + supabaseKey, 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(args || {})
    }, timeoutMs);
    if (!r.ok) {
      const raw = (r.json && typeof r.json === 'object' && !Array.isArray(r.json)) ? r.json : {};
      const err = new Error(raw.message || raw.hint || ('rpc ' + name));
      err.__hohRpcCode = String(raw.code || '');
      err.__hohRpcStatus = r.status;
      err.__hohRpcDetails = String(raw.details || '');
      err.__hohRpcHint = String(raw.hint || '');
      err.__hohRpcName = String(name || '');
      err.httpStatus = r.status;
      /* the database answered and rolled back: a new intent may take a new id */
      if (r.status >= 400 && r.status < 500 && r.status !== 408 && r.status !== 425 && r.status !== 429) err.__bgDefinite = true;
      throw err;
    }
    return Array.isArray(r.json) ? r.json[0] : r.json;
  }

  function rememberRun(data) {
    if (!data || typeof data !== 'object') return;
    const id = data.run_id != null ? data.run_id : (data.run && data.run.id != null ? data.run.id : null);
    const ver = data.engine_ver != null ? data.engine_ver : (data.run && data.run.engine_ver != null ? data.run.engine_ver : null);
    if (id != null && ver != null && (ver | 0) > 0) runEngines.set(String(id), ver | 0);
  }
  function engineFor(runId, given) {
    if (given != null && (given | 0) > 0) return given | 0;
    const known = runEngines.get(String(runId));
    return known != null ? known : null;
  }

  /* one door: auth, request id, call, classify */
  async function door(name, extra, opts) {
    const o = opts || {};
    const c = creds();
    const auth = o.auth !== false;
    if (auth && !c.ok) throw signInFirst();
    const args = auth ? { p_email: c.email, p_pin: c.pin } : {};
    Object.assign(args, extra || {});
    let j = null;
    if (o.write) {
      j = journalTake(c.account, name.replace(/^hoh_bgw_/, ''), o.intent === undefined ? extra : o.intent, o.scope);
      args.p_request_id = j.id;
    }
    pending(1);
    try {
      const data = await rpc(name, args, o.timeout || T.rpc);
      if (auth && creds().account !== c.account) throw accountChanged();
      if (!data || typeof data !== 'object') {
        const e = new Error('no_answer');
        e.bgDoor = name;
        throw e;
      }
      if (data.ok !== true) throw refusal(name, data, 0);
      if (j) journalClear(j.key);
      rememberRun(data);
      return data;
    } catch (err) {
      if (j && err && err.__bgDefinite) journalClear(j.key);
      throw err;
    } finally {
      pending(-1);
    }
  }

  /* the Worker: the reply rules of hoh-contract-data.js settleOnce */
  async function workerPost(body, opts) {
    const o = opts || {};
    const c = creds();
    if (!c.ok) throw signInFirst();
    const payload = Object.assign({}, body, { email: c.email, pin: c.pin });
    pending(1);
    try {
      const r = await send(workerUrl + '/contract-settle', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      }, o.timeout || T.worker);
      const json = r.json && typeof r.json === 'object' && !Array.isArray(r.json) ? r.json : null;
      if (r.status === 408 || r.status === 425 || r.status === 429 || r.status >= 500) {
        const eHttp = new Error('temporary settle failure (' + r.status + ')');
        eHttp.retryable = true;
        eHttp.httpStatus = r.status;
        eHttp.resultSafe = !!(json && json.result_safe);
        eHttp.settleCode = String((json && json.error) || '');
        eHttp.settleDetail = String((json && json.detail) || '').slice(0, 200);
        eHttp.deterministic = !!(json && json.deterministic);
        eHttp.__hocDeterministic = eHttp.deterministic;
        throw eHttp;
      }
      if (!json) {
        const eBody = new Error('bad settle body (' + r.status + ')');
        eBody.retryable = true;
        eBody.httpStatus = r.status;
        throw eBody;
      }
      if (creds().account !== c.account) throw accountChanged();
      if (json.ok !== true) {
        const eRef = new Error(json.error || json.reason || 'settle refused');
        eRef.__hocRefused = json.error || json.reason || 'refused';
        eRef.settleCode = String(json.error || json.reason || '');
        eRef.settleDetail = String(json.detail || '').slice(0, 200);
        eRef.__hocDeterministic = json.deterministic === true;
        if (eRef.__hocDeterministic) eRef.deterministic = true;
        eRef.bgReason = eRef.settleCode;
        eRef.httpStatus = r.status;
        eRef.reply = json;
        eRef.retryable = false;
        throw eRef;
      }
      if (o.runId != null && json.run && json.run.id != null && String(json.run.id) !== String(o.runId)) {
        throw new Error('The server answered for a different run. Reconnect to try again.');
      }
      rememberRun(json);
      return json;
    } finally {
      pending(-1);
    }
  }

  async function workerGate(runVersion) {
    const v = runVersion | 0;
    const need = v >= 115 ? Math.max(LEVEL_MIN_BUILD, cfg.minWorkerBuild | 0) : ENDLESS_MIN_BUILD;
    let probe = gateCache && Date.now() - gateCache.at < 10000 ? gateCache : null;
    if (!probe) {
      pending(1);
      try {
        const url = workerUrl + '/contract-settle?gate=' + encodeURIComponent('bgw' + v + '-V' + need + '-' + Date.now());
        const r = await send(url, { method: 'GET' }, T.gate);
        const json = r.json;
        if (!r.ok || !json || json.ok !== true) throw new Error('battle server check failed');
        const m = /^V([0-9]+)$/.exec(String(json.build || ''));
        probe = {
          at: Date.now(),
          worker_engine: json.worker_ver | 0,
          worker_build: m ? (parseInt(m[1], 10) || 0) : 0,
          supported_versions: Array.isArray(json.supported_versions) ? json.supported_versions.map(x => x | 0) : [json.worker_ver | 0]
        };
        gateCache = probe;
      } catch (_) {
        const unavailable = new Error('We could not check the battle server. Check your connection and try again.');
        unavailable.__hohWorkerGate = true;
        throw unavailable;
      } finally {
        pending(-1);
      }
    }
    const result = { ok: true, run_version: v, worker_engine: probe.worker_engine, worker_build: probe.worker_build,
      supported_versions: probe.supported_versions.slice(), minimum_build: need };
    if (v > 0 && probe.supported_versions.indexOf(v) < 0) {
      const mismatch = new Error('The battle server is being updated. Your run is saved. Try again shortly.');
      mismatch.__hohWorkerGate = true;
      mismatch.reason = 'worker_run_version_mismatch';
      mismatch.gate = result;
      throw mismatch;
    }
    if (probe.worker_build < need) {
      const old = new Error('The battle server is being updated. Your run is saved. Try again shortly.');
      old.__hohWorkerGate = true;
      old.reason = 'worker_build_too_old';
      old.gate = result;
      throw old;
    }
    return result;
  }

  function rememberCatalog(data, persist) {
    catalogMem.set(data.content_sha, data);
    catalogLast = data;
    if (persist) {
      const key = CATALOG_PREFIX + data.content_sha;
      if (lsSet(key, JSON.stringify(data))) { lsSet(CATALOG_LAST, data.content_sha); lsDropPrefix(CATALOG_PREFIX, key); }
    }
  }
  async function catalog(opts) {
    const o = opts || {};
    const want = o.sha ? String(o.sha) : '';
    if (want && catalogMem.has(want)) return catalogMem.get(want);
    if (!want && !o.fresh && catalogLast) return catalogLast;
    if (!o.fresh) {
      const sha = want || lsGet(CATALOG_LAST);
      if (sha) {
        let cached = null;
        try { cached = JSON.parse(lsGet(CATALOG_PREFIX + sha) || 'null'); } catch (_) { cached = null; }
        if (cached && cached.ok === true && cached.content_sha === sha) { rememberCatalog(cached, false); return cached; }
      }
    }
    const data = await door('hoh_bgw_catalog', {}, { auth: false });
    if (typeof data.content_sha !== 'string' || !data.content_sha) {
      const e = new Error('catalog_without_content_sha');
      throw e;
    }
    rememberCatalog(data, true);
    return data;
  }

  /* The Hall's public roster read (hoh-section-core.js: approved, not hidden), for faces only:
     the pack fans and the admin's poster search. No account, no request id. */
  async function hallHeroes(o) {
    const q = o && typeof o === 'object' ? o : {};
    const qs = [['select', HALL_COLS], ['approved', 'eq.true'], ['or', '(is_hidden.is.null,is_hidden.eq.false)']];
    if (q.ff === true) qs.push(['is_ff_portrait', 'eq.true']);
    else if (q.ff === false) qs.push(['is_ff_portrait', 'not.is.true']);
    const name = String(q.query || '').normalize('NFKC').replace(/[^\p{L}\p{N}' -]+/gu, ' ').replace(/\s+/g, ' ').trim().slice(0, 40);
    if (name) qs.push(['name', 'ilike.*' + name + '*']);
    qs.push(['order', q.ff == null ? 'is_ff_portrait.desc.nullslast,created_at.desc,id.desc' : 'created_at.desc,id.desc']);
    qs.push(['limit', String(Math.max(1, Math.min(60, (q.limit | 0) || 24)))]);
    pending(1);
    try {
      const r = await send(supabaseUrl + '/rest/v1/characters?' + qs.map(kv => kv[0] + '=' + encodeURIComponent(kv[1])).join('&'), {
        method: 'GET', headers: { apikey: supabaseKey, Authorization: 'Bearer ' + supabaseKey, Accept: 'application/json' }
      }, T.rpc);
      if (!r.ok || !Array.isArray(r.json)) {
        const raw = r.json && typeof r.json === 'object' && !Array.isArray(r.json) ? r.json : {};
        const err = new Error(raw.message || 'hall read');
        err.__hohRpcCode = String(raw.code || ''); err.__hohRpcName = 'characters'; err.httpStatus = r.status;
        throw err;
      }
      return { ok: true, heroes: r.json.map(hallFace).filter(Boolean) };
    } finally {
      pending(-1);
    }
  }

  /* R800: named Hall heroes by character id (the cutscenes' cast, a boss's own faces): the same public columns and
     filters as hallHeroes, at most 60 ids. No account, no request id. */
  async function hallFaces(ids) {
    const list = Array.from(new Set((Array.isArray(ids) ? ids : []).map(x => String(x || '').toLowerCase()).filter(x => /^[0-9a-f-]{36}$/.test(x)))).slice(0, 60);
    if (!list.length) return { ok: true, heroes: [] };
    const qs = [['select', HALL_COLS], ['approved', 'eq.true'], ['or', '(is_hidden.is.null,is_hidden.eq.false)'], ['id', 'in.(' + list.join(',') + ')'], ['limit', String(list.length)]];
    pending(1);
    try {
      const r = await send(supabaseUrl + '/rest/v1/characters?' + qs.map(kv => kv[0] + '=' + encodeURIComponent(kv[1])).join('&'), {
        method: 'GET', headers: { apikey: supabaseKey, Authorization: 'Bearer ' + supabaseKey, Accept: 'application/json' }
      }, T.rpc);
      if (!r.ok || !Array.isArray(r.json)) {
        const raw = r.json && typeof r.json === 'object' && !Array.isArray(r.json) ? r.json : {};
        const err = new Error(raw.message || 'hall faces');
        err.__hohRpcCode = String(raw.code || ''); err.__hohRpcName = 'characters'; err.httpStatus = r.status;
        throw err;
      }
      return { ok: true, heroes: r.json.map(hallFace).filter(Boolean) };
    } finally {
      pending(-1);
    }
  }

  /* R800 (self-review D2): every hero the Hall shows, for the hosted battle's faces (the Hall page's
     hohGetAllCharacters, which this page does not load): a level boss's poster hero, Call for Aid and
     the recruits. The public columns only, in pages of 1000. No account, no request id. */
  async function hallRoster() {
    const out = [];
    for (let from = 0; from < 10000; from += 1000) {
      const qs = [['select', ROSTER_COLS], ['approved', 'eq.true'], ['or', '(is_hidden.is.null,is_hidden.eq.false)'], ['order', 'created_at.desc,id.desc'], ['limit', '1000'], ['offset', String(from)]];
      pending(1);
      let r;
      try {
        r = await send(supabaseUrl + '/rest/v1/characters?' + qs.map(kv => kv[0] + '=' + encodeURIComponent(kv[1])).join('&'), {
          method: 'GET', headers: { apikey: supabaseKey, Authorization: 'Bearer ' + supabaseKey, Accept: 'application/json' }
        }, T.long);
      } finally { pending(-1); }
      if (!r.ok || !Array.isArray(r.json)) {
        const raw = r.json && typeof r.json === 'object' && !Array.isArray(r.json) ? r.json : {};
        const err = new Error(raw.message || 'hall roster');
        err.__hohRpcCode = String(raw.code || ''); err.__hohRpcName = 'characters'; err.httpStatus = r.status;
        throw err;
      }
      for (const row of r.json) if (row && row.id != null) out.push(row);
      if (r.json.length < 1000) break;
    }
    return { ok: true, heroes: out };
  }

  /* R800 + BGSHELL (the guest Prologue): three FF Hall heroes a visitor with no account can borrow. Prefer three
     different classes, then fill from the remaining FF portraits; rotate the ordered pool each day. The public
     columns only, no owner; no account, no request id. */
  const GUEST_CLASSES = ['Artificer', 'Barbarian', 'Bard', 'Cleric', 'Druid', 'Fighter', 'Monk', 'Paladin', 'Ranger', 'Rogue', 'Sorcerer', 'Warlock', 'Wizard'];
  async function guestLoaners() {
    const qs = [['select', ROSTER_COLS], ['approved', 'eq.true'], ['or', '(is_hidden.is.null,is_hidden.eq.false)'], ['is_ff_portrait', 'eq.true'],
      ['order', 'hearts.asc,id.asc'], ['limit', '300']];
    pending(1);
    let r;
    try {
      r = await send(supabaseUrl + '/rest/v1/characters?' + qs.map(kv => kv[0] + '=' + encodeURIComponent(kv[1])).join('&'), {
        method: 'GET', headers: { apikey: supabaseKey, Authorization: 'Bearer ' + supabaseKey, Accept: 'application/json' }
      }, T.rpc);
    } finally { pending(-1); }
    if (!r.ok || !Array.isArray(r.json)) {
      const raw = r.json && typeof r.json === 'object' && !Array.isArray(r.json) ? r.json : {};
      const err = new Error(raw.message || 'guest loaners');
      err.__hohRpcCode = String(raw.code || ''); err.__hohRpcName = 'characters'; err.httpStatus = r.status;
      throw err;
    }
    const day = Math.floor(Date.now() / 86400000);
    const pool = r.json.filter(x => x && x.id && (x.image_thumb_url || x.image_url) && x.is_ff_portrait === true);
    const start = pool.length ? day % pool.length : 0, ordered = pool.slice(start).concat(pool.slice(0, start));
    const chosen = [], used = new Set();
    const clsOf = x => GUEST_CLASSES.find(c => new RegExp('\\b' + c + '\\b', 'i').test(String(x.character_class || ''))) || 'Fighter';
    for (const x of ordered) { const cls = clsOf(x); if (!used.has(cls)) { chosen.push(x); used.add(cls); if (chosen.length === 3) break; } }
    for (const x of ordered) if (chosen.length < 3 && !chosen.includes(x)) chosen.push(x);
    const out = chosen.slice(0, 3).map(x => { const cls = clsOf(x); return { character_id: String(x.id), name: String(x.name || 'A Hall hero'), cls: cls,
      class_text: String(x.character_class || cls), race: String(x.race || ''), img: String(x.image_thumb_url || x.image_url || ''),
      frame: { x: x.image_offset_x, y: x.image_offset_y, zoom: x.image_zoom }, is_ff: true, owner: null }; });
    return { ok: true, loaners: out };
  }

  /* R812: the class paths (hoh_bgw_class_paths120, immutable per release): one read per page, shared by every caller
     while it is in flight; a failed read is tried again by the next caller */
  let pathsMem = null, pathsWait = null;
  function classPaths() {
    if (pathsMem) return Promise.resolve(pathsMem);
    if (pathsWait) return pathsWait;
    /* the content door returns the file itself (§9.2), which need not carry ok: true, so it is read as a bare rpc */
    pending(1);
    pathsWait = rpc('hoh_bgw_class_paths120', {}, T.rpc).then(data => {
      pending(-1);
      pathsWait = null;
      if (!data || typeof data !== 'object' || data.ok === false || !data.paths) throw new Error('class_paths_without_paths');
      pathsMem = data;
      /* the battle screen (hoh-contract-ui, a classic script) names path rows from this; it only reads it */
      try { window.HOHClassPaths120 = data; } catch (_) {}
      return data;
    }, err => { pending(-1); pathsWait = null; throw err; });
    return pathsWait;
  }

  const L = () => T.long;
  return Object.freeze({
    session() { const c = creds(); return { signedIn: c.ok, account: c.account }; },
    catalog: catalog,
    home: () => door('hoh_bgw_home_state', {}),
    cards: (o) => door('hoh_bgw_cards_list', {
      p_after: o && o.after != null ? String(o.after) : null,
      p_limit: o && o.limit != null ? Number(o.limit) : 60
    }),
    card: (key) => door('hoh_bgw_card_get', { p_card_key: String(key || '') }),
    saveLoadout: (o) => door('hoh_bgw_loadout_set', {
      p_slots: (o && o.slots) || [], p_seats: (o && o.seats) || [], p_campfire: (o && o.campfire) || []
    }, { write: true }),
    /* hoh_bgw_class_choice(p_email, p_pin, p_character, p_class) takes no request id (R806, kept by R812 §6): PostgREST
       finds a function by its argument names, so a p_request_id would be answered PGRST202 and no pick would save */
    saveClass: (characterId, cls) => door('hoh_bgw_class_choice', {
      p_character: String(characterId || ''), p_class: String(cls || '')
    }),
    // An idempotent setting, not a purchase/reward: this RPC does not take a request journal id.
    saveTalent: (key, tier, choice) => door('hoh_bgw_champion_choose119', {
      p_card_key: String(key || ''), p_tier: Number(tier), p_choice: String(choice || '')
    }),
    startLevel: (level) => door('hoh_bgw_level_start', { p_level: Number(level) }, { write: true, timeout: L() }),
    /* the assisted attempt's one free reroll of a pending upgrade offer (P26); the Worker's next offer draws again */
    assistReroll: (runId) => door('hoh_bgw_assist_reroll', { p_run_id: Number(runId) || 0 }, { write: true }),
    runState: () => door('hoh_bg_unlimited_state', {}),
    /* the continue reply names no engine; a child run always keeps its parent's (the runtime guard binds it) */
    continueRun: (parentRunId) => door('hoh_bg_unlimited_continue', { p_parent_run_id: Number(parentRunId) || 0 }, { timeout: L() })
      .then(data => {
        const ver = engineFor(parentRunId, null);
        if (ver != null && data.run_id != null && !runEngines.has(String(data.run_id))) runEngines.set(String(data.run_id), ver);
        return data;
      }),
    endRun: (runId) => door('hoh_bg_unlimited_forfeit', { p_run_id: Number(runId) || 0 }),
    settle(runId, o) {
      const opt = o || {};
      const body = { run_id: Number(runId), outcome: 'battle',
        plays: Array.isArray(opt.plays) && opt.plays.length ? opt.plays : undefined,
        recruit_faces: opt.recruit_faces == null ? null : opt.recruit_faces,
        engine_ver: engineFor(runId, opt.engine_ver) };
      return workerPost(body, { runId: runId });
    },
    flee(runId, lastCompletedRound, o) {
      const body = { run_id: Number(runId), outcome: 'solo_forfeit', last_completed_round: lastCompletedRound | 0,
        engine_ver: engineFor(runId, o && o.engine_ver) };
      return workerPost(body, { runId: runId });
    },
    upgrade(action, runId, fields) {
      if (UPGRADE_ACTIONS.indexOf(action) < 0) return Promise.reject(new Error('unknown_upgrade_action'));
      const body = Object.assign({}, fields || {}, { action: action, run_id: Number(runId),
        engine_ver: engineFor(runId, fields && fields.engine_ver) });
      return workerPost(body, { runId: runId });
    },
    merge: (target, fodder, confirm) => door('hoh_bgw_merge', {
      p_target_key: String(target || ''), p_fodder: Array.isArray(fodder) ? fodder : [], p_confirm_valuable: !!confirm
    }, { write: true, timeout: L() }),
    reroll: (key, line) => door('hoh_bgw_potential_reroll', { p_card_key: String(key || ''), p_line: Number(line) }, { write: true }),
    resolveReroll: (key, take) => door('hoh_bgw_potential_resolve', { p_card_key: String(key || ''), p_take: !!take }, { write: true }),
    buyPack: (pack, count) => door('hoh_bgw_pack_buy', { p_pack: String(pack || ''), p_count: count == null ? 1 : Number(count) }, { write: true }),
    openPack: (pack) => door('hoh_bgw_pack_open', { p_pack: String(pack || '') }, { write: true }),
    buySeat: (seat) => door('hoh_bgw_seat_buy', { p_seat: Number(seat) }, { write: true }),
    upgradeSeat: (seat) => door('hoh_bgw_seat_upgrade', { p_seat: Number(seat) }, { write: true }),
    claimStarChest: (at) => door('hoh_bgw_star_chest_claim', { p_at: Number(at) }, { write: true }),
    /* the Shop's gift: one intent per Pacific day, so a weather retry can never replay yesterday's receipt */
    claimDailyGift: () => door('hoh_bgw_daily_gift_claim', {}, { write: true, intent: { day: pacificDay() } }),
    /* R813: the Shop's free Common Pack, once per Pacific day, lands in the held packs (home.free_pack; open it with openPack) */
    claimFreePack: () => door('hoh_bgw_free_pack_claim', {}, { write: true, intent: { day: pacificDay() } }),
    /* R812 class seats and the Daily calendar (docs/battlegrounds/CLASS-SLOTS-CONTRACT-2026-09-28.md §9.5). The page calls
       them only once home.classes.state is 'ready' (the class doors) or home.daily is there (the Daily); before R812 they
       are never reached. classPaths is the content door: no account, read once per page and kept */
    classPaths: () => classPaths(),
    slotSet: (slot, cls, faceKey) => door('hoh_bgw_slot_set', {
      p_slot: Number(slot), p_cls: String(cls || '').toLowerCase(), p_face_key: faceKey ? String(faceKey) : null
    }, { write: true }),
    slotFeed: (slot, cards, freeXp, confirmLast) => door('hoh_bgw_slot_feed', {
      p_slot: Number(slot), p_cards: Array.isArray(cards) ? cards : [], p_free_xp: Math.max(0, Number(freeXp) || 0), p_confirm_last: !!confirmLast
    }, { write: true, timeout: L() }),
    feedSpares: () => door('hoh_bgw_slot_feed_spares', {}, { write: true, timeout: L() }),
    makeFreeXp: (cards, confirmLast) => door('hoh_bgw_free_xp_make', {
      p_cards: Array.isArray(cards) ? cards : [], p_confirm_last: !!confirmLast
    }, { write: true }),
    revealAck: (slot, cls, level) => door('hoh_bgw_slot_reveal_ack', {
      p_slot: Number(slot), p_cls: String(cls || '').toLowerCase(), p_level: Number(level)
    }, { write: true, scope: 'reveal:' + slot + ':' + String(cls || '').toLowerCase() }),
    setEcho: (slot, echo) => door('hoh_bgw_trait_echo_set', {
      p_slot: Number(slot), p_echo: echo ? String(echo).toLowerCase() : null
    }, { write: true, scope: 'echo:' + slot }),
    slotsIntroAck: () => door('hoh_bgw_slots_intro_ack', {}, { write: true }),
    /* one Daily claim a Pacific day pays the calendar day and the free Common Pack together */
    claimDaily: () => door('hoh_bgw_daily_claim', {}, { write: true, intent: { day: pacificDay() } }),
    /* R817 (docs/battlegrounds/R817-REWARDS-ROAD.md §2): a cleared chapter's chest, once. One journal intent per chapter
       (the scope), so a retry of one chest never replays another chest's receipt; the caller checks reply.chapter. The
       page calls it only while home.chests_waiting says a chest is ready, so a server before R817 never sees it */
    claimChapterChest: (id) => door('hoh_bgw_chapter_chest_claim', { p_chapter: String(id) }, { write: true, scope: 'chest:' + String(id) }),
    /* R800 onboarding (§11): a loaner Champion for the Prologue, offered by home.loaners (the server's
       small SQL; the page reads it only when it is there) */
    /* R800 (§11, docs/R801-ENGINE117-PLAN.md §6.1): Level 1 with a borrowed loaner Champion; answers as startLevel plus loaner */
    prologueStart: (characterId) => door('hoh_bgw_prologue_start', { p_character: String(characterId || '') }, { write: true, timeout: L() }),
    /* the player's star: a set, not a toggle; the other value's unanswered intent is dropped */
    favoriteCard: (key, favorite) => door('hoh_bgw_card_favorite', { p_card_key: String(key || ''), p_favorite: !!favorite },
      { write: true, scope: 'card:' + String(key || '') }),
    ledger: (before) => door('hoh_bgw_ledger_list', { p_before: before == null ? null : Number(before) }),
    adminRarity(query) {
      const q = query && typeof query === 'object' ? query : { query: query };
      return door('hoh_bgw_admin_rarity_list', {
        p_query: q.query == null || q.query === '' ? null : String(q.query),
        p_rarity: q.rarity == null ? null : Number(q.rarity),
        p_after: q.after == null ? null : String(q.after),
        p_limit: q.limit == null ? 100 : Number(q.limit)
      });
    },
    adminSetRarity: (changes) => door('hoh_bgw_admin_rarity_set', { p_changes: Array.isArray(changes) ? changes : [] }, { write: true }),
    adminWorld: (o) => door('hoh_bgw_admin_world', {
      p_open: o && typeof o.open === 'boolean' ? o.open : null,
      p_arm_reset: o && typeof o.armReset === 'boolean' ? o.armReset : null
    }),
    /* the Hall hero a level boss wears (null clears it); home.bosses[].poster is the live answer */
    adminBossPoster: (bossId, characterId) => door('hoh_bgw_admin_boss_poster', {
      p_boss_id: String(bossId || ''), p_character_id: characterId ? String(characterId) : null
    }, { write: true, scope: 'boss:' + String(bossId || '') }),
    hallHeroes: hallHeroes,
    hallRoster: hallRoster,
    hallFaces: hallFaces,
    guestLoaners: guestLoaners,
    workerGate: workerGate
  });
}
